/**
 * Light / dark theme — system-aware by default, manual override persists in
 * @capacitor/preferences (web → localStorage, native → SharedPreferences).
 *
 * Usage:
 *   const { theme, toggle, setTheme } = useTheme();
 *   <button @click="toggle">...</button>
 */
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { Preferences } from '@capacitor/preferences';

export type Theme = 'light' | 'dark';
type Choice = Theme | 'system';

const THEME_KEY = 'wattr.theme.v1';

// Single shared state across all composable callers so the toggle in the
// header re-renders every component without prop-drilling.
const choice = ref<Choice>('system');
const resolved = ref<Theme>('light');
let mql: MediaQueryList | null = null;

function applyToDom(t: Theme) {
  document.documentElement.setAttribute('data-theme', t);
  document.documentElement.style.colorScheme = t;
}

function pickResolved(): Theme {
  if (choice.value === 'light' || choice.value === 'dark') return choice.value;
  return mql?.matches ? 'dark' : 'light';
}

function syncResolved() {
  resolved.value = pickResolved();
  applyToDom(resolved.value);
}

async function load() {
  try {
    const { value } = await Preferences.get({ key: THEME_KEY });
    if (value === 'light' || value === 'dark' || value === 'system') {
      choice.value = value;
    }
  } catch {
    /* ignore */
  }
  syncResolved();
}

async function persist() {
  try {
    await Preferences.set({ key: THEME_KEY, value: choice.value });
  } catch {
    /* ignore */
  }
}

export function useTheme() {
  onMounted(async () => {
    if (!mql) {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.addEventListener('change', syncResolved);
    }
    await load();
  });

  onBeforeUnmount(() => {
    // We leave the listener attached — singleton store, no leak per instance.
  });

  watch(choice, () => {
    syncResolved();
    void persist();
  });

  function setTheme(t: Choice) {
    choice.value = t;
  }

  function toggle() {
    // light → dark → system → light
    choice.value =
      resolved.value === 'light' ? 'dark' : choice.value === 'dark' ? 'system' : 'light';
  }

  return { choice, theme: resolved, setTheme, toggle };
}
